import { shareValueBuyPlusPnl } from "./investmentPricing";

export function formatDecimal(value, fractionDigits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

export function formatBdtAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-BD", {
    style: "currency",
    currency: "BDT",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}

export function formatPnlBdt(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `BDT ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function pnlStatusBadgeKey(row) {
  return String(row?.display_status || row?.status || "").toUpperCase();
}

export function pnlStatusLabel(rowOrStatus) {
  const s =
    rowOrStatus && typeof rowOrStatus === "object"
      ? pnlStatusBadgeKey(rowOrStatus)
      : String(rowOrStatus || "").toUpperCase();
  if (s === "APPROVED") return "Approved";
  if (s === "REJECTED") return "Rejected";
  if (s === "CANCELLED") return "Cancelled";
  if (s === "UPDATED") return "Updated";
  if (s === "PENDING") return "Pending";
  return s || "—";
}

export function navPerShareFromProject(p) {
  return formatDecimal(shareValueBuyPlusPnl(p), 2);
}

export function availablePrimaryShares(p) {
  const total = Number(p?.total_shares ?? p?.totalShares ?? 0);
  const sold = Number(p?.shares_sold ?? p?.sharesSold ?? 0);
  if (!Number.isFinite(total) || !Number.isFinite(sold)) return "—";
  return formatDecimal(Math.max(0, total - sold), 2);
}

export function isPrimaryFundraisingStage(project) {
  const s = String(project?.status ?? "").toUpperCase();
  return s === "FUNDING" || s === "PAUSED";
}

export function pnlNavDenominatorShares(project) {
  if (isPrimaryFundraisingStage(project)) {
    const sold = Number(project?.shares_sold ?? project?.sharesSold ?? 0);
    if (!Number.isFinite(sold) || sold <= 0) return 0;
    return Math.round(sold * 100) / 100;
  }
  const ts = Number(project?.total_shares ?? project?.totalShares ?? 0);
  if (!Number.isFinite(ts) || ts <= 0) return 0;
  return Math.round(ts * 100) / 100;
}

export function totalShareCap(project) {
  const ts = Number(project?.total_shares ?? project?.totalShares ?? 0);
  return Number.isFinite(ts) && ts > 0 ? Math.round(ts * 100) / 100 : 0;
}

export function currentNavBdt(project) {
  const api = project?.share_value_bdt ?? project?.shareValueBdt;
  if (api != null && api !== "") {
    const n = Number(api);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const cur = Number(project?.current_share_price ?? project?.currentSharePrice ?? 0);
  if (Number.isFinite(cur) && cur > 0) return cur;
  const initial = Number(project?.initial_share_price ?? project?.initialSharePrice ?? 0);
  return Number.isFinite(initial) ? initial : 0;
}

export function roundHalfUp2(value) {
  const x = Number(value);
  if (!Number.isFinite(x)) return null;
  if (x >= 0) return Math.floor(x * 100 + 0.5) / 100;
  return -Math.floor(-x * 100 + 0.5) / 100;
}

export function navAfterPnlBdt(project, netPnlBdt) {
  const denom = pnlNavDenominatorShares(project);
  if (denom <= 0) return null;
  const net = Number(netPnlBdt);
  if (!Number.isFinite(net)) return null;
  return roundHalfUp2(currentNavBdt(project) + net / denom);
}

export function targetFundAfterPnlBdt(project, netPnlBdt) {
  const nav = navAfterPnlBdt(project, netPnlBdt);
  const cap = totalShareCap(project);
  if (nav == null || cap <= 0) return null;
  return roundHalfUp2(nav * cap);
}

export function netAssetsOnIssuedAfterPnlBdt(project, netPnlBdt) {
  const nav = navAfterPnlBdt(project, netPnlBdt);
  const sold = Number(project?.shares_sold ?? project?.sharesSold ?? 0);
  if (nav == null || !Number.isFinite(sold) || sold <= 0) return null;
  return roundHalfUp2(nav * sold);
}

export const emptyPnlForm = () => ({
  project: "",
  submission_date: "",
  revenue: "",
  expenses: "",
  admin_fee: "",
  updated_fund_value: "",
  narrative: "",
});

function parseDateOnly(iso) {
  if (!iso) return null;
  const parts = String(iso).slice(0, 10).split("-");
  if (parts.length !== 3) return null;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return new Date(y, m - 1, d);
}

function formatDateOnlyLocal(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function todayDateOnlyLocal() {
  return formatDateOnlyLocal(new Date());
}

function maxIsoDateOnly(a, b) {
  if (!a) return b || "";
  if (!b) return a || "";
  return a > b ? a : b;
}

export function earliestPnlSubmissionDateIso(project, pnlRows = []) {
  const api = project?.pnl_earliest_submission_date ?? project?.pnlEarliestSubmissionDate;
  if (api) return String(api).slice(0, 10);

  let earliest = parseDateOnly(project?.fundraising_start ?? project?.fundraisingStart);
  if (!earliest) earliest = parseDateOnly(todayDateOnlyLocal());

  const projectId = String(project?.id ?? "");
  const approved = pnlRows.filter(
    (r) => String(r.project) === projectId && String(r.status || "").toUpperCase() === "APPROVED"
  );
  for (const row of approved) {
    const end = parseDateOnly(row.period_end ?? row.periodEnd);
    if (!end) continue;
    const next = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1);
    if (!earliest || next > earliest) earliest = next;
  }
  return formatDateOnlyLocal(earliest);
}

export function defaultPnlSubmissionDateIso(project, pnlRows = []) {
  return maxIsoDateOnly(earliestPnlSubmissionDateIso(project, pnlRows), todayDateOnlyLocal());
}

export function clampPnlSubmissionDateIso(value, project, pnlRows = []) {
  const min = earliestPnlSubmissionDateIso(project, pnlRows);
  if (!min) return value || defaultPnlSubmissionDateIso(project, pnlRows);
  if (!value || value < min) return defaultPnlSubmissionDateIso(project, pnlRows);
  return value;
}

export function isPnlEligibleStage(project) {
  const status = String(project?.status || "").toUpperCase();
  return ["FUNDING", "PAUSED", "LOCK_IN"].includes(status);
}

export function projectStageLabel(project) {
  const status = String(project?.status || "").toUpperCase();
  if (["DRAFT"].includes(status)) return "Draft";
  if (["REVIEW", "OPEN"].includes(status)) return "Review (Admin / compliance)";
  if (["FUNDING", "PAUSED"].includes(status)) return "Fundraising";
  if (["LOCK_IN"].includes(status)) return "Funded";
  if (status === "CANCELLED") return "Cancelled";
  if (status === "REDEEMED" || status === "COMPLETED") return "Redeemed";
  if (status === "MATURED") return "Matured";
  if (status === "CLOSED") return "Closed";
  return "Unmapped";
}

export function isApprovedPnl(row) {
  return pnlStatusBadgeKey(row) === "APPROVED";
}

/** Submitted P&L that is not approved yet (pending, updated, rejected, cancelled). */
export function nonApprovedPnlRows(pnlRows = []) {
  return pnlRows.filter((r) => !isApprovedPnl(r));
}

export function approvedPnlRows(pnlRows = []) {
  return pnlRows.filter((r) => isApprovedPnl(r));
}

export function nonApprovedPnlForProject(projectId, pnlRows = []) {
  return nonApprovedPnlRows(pnlRows).filter((r) => String(r.project) === String(projectId));
}

export function pendingPnlForProject(projectId, pnlRows = []) {
  return pnlRows.filter(
    (r) => String(r.project) === String(projectId) && String(r.status || "").toUpperCase() === "PENDING"
  );
}

export function sortPnlRowsNewestFirst(rows = []) {
  return [...rows].sort(
    (a, b) => new Date(b.submitted_at || 0).getTime() - new Date(a.submitted_at || 0).getTime()
  );
}

export function formatPnlSubmitError(err) {
  const detail = err?.response?.data;
  if (typeof detail === "string" && detail) return detail;
  if (detail?.detail) return String(detail.detail);
  if (Array.isArray(detail?.non_field_errors) && detail.non_field_errors[0]) {
    return String(detail.non_field_errors[0]);
  }
  if (detail && typeof detail === "object") {
    const firstKey = Object.keys(detail)[0];
    const val = detail[firstKey];
    if (Array.isArray(val) && val[0]) return `${firstKey}: ${val[0]}`;
    if (typeof val === "string") return `${firstKey}: ${val}`;
  }
  return "Failed to submit P&L.";
}

export const ASSET_MANAGER_HISTORY_SECTIONS = ["pnl", "commissions", "transactions"];

export const ASSET_MANAGER_HISTORY_META = {
  pnl: { label: "Approved P&L", description: "P&L submissions approved by admin for your assigned projects." },
  commissions: {
    label: "Commission history",
    description: "Your commission on approved P&L (rate set by Super Admin in Settings).",
  },
  transactions: {
    label: "Transaction history",
    description:
      "Your operational activity: P&L submissions, commission, wallet withdrawals/deposits, and admin project fund adjustments.",
  },
};

export const AM_ACTIVITY_CATEGORY_LABELS = {
  PNL: "P&L",
  COMMISSION: "Commission",
  WITHDRAWAL: "Withdrawal",
  DEPOSIT: "Wallet deposit",
  PROJECT_FUND: "Project fund",
};

export function amActivityCategoryLabel(category) {
  const key = String(category || "").toUpperCase();
  return AM_ACTIVITY_CATEGORY_LABELS[key] || key || "—";
}
